/**
 * Bulk import dialogue transcript: parse, match voices, generate all translations, save.
 */
import React, { useState } from 'react';
import { FiCheck, FiRefreshCw, FiX } from 'react-icons/fi';
import { dialogueBulkContentAPI } from '../services/api';
import type { BulkContentItem, DialoguePartAudio } from '../services/api';
import './DialogueBulkContentModal.css';

interface DialogueBulkContentModalProps {
  topicId: number;
  partId: number;
  partName: string;
  audios: DialoguePartAudio[];
  onClose: () => void;
  onSaved?: () => void;
}

const DialogueBulkContentModal: React.FC<DialogueBulkContentModalProps> = ({
  topicId,
  partId,
  partName,
  audios,
  onClose,
  onSaved,
}) => {
  const [transcript, setTranscript] = useState('');
  const [items, setItems] = useState<BulkContentItem[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState<'paste' | 'review'>('paste');

  const voiceCount = audios.length;

  const handleParse = async () => {
    if (!transcript.trim()) {
      setError('Paste the dialogue transcript first');
      return;
    }
    if (voiceCount === 0) {
      setError('Add voice clips to this part before importing content');
      return;
    }
    setParsing(true);
    setError(null);
    try {
      const res = await dialogueBulkContentAPI.parse(topicId, partId, transcript);
      setItems(res.items || []);
      setWarnings(res.warnings || []);
      setStep('review');
    } catch (err: unknown) {
      setError((err as Error).message || 'Parse failed');
    } finally {
      setParsing(false);
    }
  };

  const handleTranslateAll = async () => {
    const withText = items.filter((i) => i.source_text.trim());
    if (withText.length === 0) {
      setError('No source text to translate');
      return;
    }
    setTranslating(true);
    setError(null);
    try {
      const res = await dialogueBulkContentAPI.translate(topicId, partId, withText);
      const byId = new Map((res.items || []).map((i) => [i.audio_id, i]));
      setItems((prev) =>
        prev.map((item) => {
          const updated = byId.get(item.audio_id);
          return updated ? { ...item, ...updated } : item;
        }),
      );
      if (res.errors?.length) {
        setWarnings(res.errors.map((e) => `${e.file_name || e.audio_id}: ${e.error}`));
      }
    } catch (err: unknown) {
      setError((err as Error).message || 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  const handleSaveAll = async () => {
    const toSave = items.filter((i) => i.source_text.trim() && i.translations.length > 0);
    if (toSave.length === 0) {
      setError('Generate translations first, or ensure each clip has source text');
      return;
    }
    if (!window.confirm(`Save content for ${toSave.length} voice clip(s)?`)) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await dialogueBulkContentAPI.save(topicId, partId, toSave);
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      setError((err as Error).message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateItemSource = (audioId: number, source_text: string) => {
    setItems((prev) =>
      prev.map((i) => (i.audio_id === audioId ? { ...i, source_text } : i)),
    );
  };

  const updateTranslation = (audioId: number, index: number, value: string) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i.audio_id !== audioId) return i;
        const translations = [...i.translations];
        translations[index] = value;
        return { ...i, translations };
      }),
    );
  };

  const langLabel = (code: string) => (code === 'fa' ? 'Persian' : 'English');

  return (
    <div className="dialogue-bulk-overlay" onClick={onClose}>
      <div className="dialogue-bulk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dialogue-bulk-header">
          <div>
            <h3>Bulk content — {partName || `Part ${partId}`}</h3>
            <p className="dialogue-bulk-sub">
              {voiceCount} voice clip{voiceCount !== 1 ? 's' : ''} · Briefing is not included
            </p>
          </div>
          <button type="button" className="dialogue-bulk-close" onClick={onClose}>
            {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 20 })}
          </button>
        </div>

        <div className="dialogue-bulk-body">
          {error && (
            <div className="dialogue-bulk-error" onClick={() => setError(null)}>
              {error}
            </div>
          )}
          {warnings.length > 0 && (
            <div className="dialogue-bulk-warnings">
              {warnings.map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </div>
          )}

          {step === 'paste' ? (
            <>
              <p className="dialogue-bulk-hint">
                Paste a <strong>Dialogue Transcript</strong> in either format:
                (1) speaker blocks with printed translations (translation lines are ignored), or
                (2) numbered lines like <code>1. Ms. Lane: Hello...</code>.
                Language (EN/FA) is detected per clip automatically.
              </p>
              <textarea
                className="dialogue-bulk-textarea"
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                placeholder="Dialogue Transcript&#10;Mr. John Smith:&#10;Welcome to...&#10;به آژانس...&#10;The Persian-speaking client:&#10;..."
                rows={14}
              />
              <div className="dialogue-bulk-actions">
                <button
                  type="button"
                  className="dialogue-bulk-btn primary"
                  onClick={() => void handleParse()}
                  disabled={parsing || voiceCount === 0}
                >
                  {parsing
                    ? React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                        className: 'spinner',
                        size: 14,
                      })
                    : 'Parse & match to voices'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="dialogue-bulk-toolbar">
                <button
                  type="button"
                  className="dialogue-bulk-btn secondary"
                  onClick={() => setStep('paste')}
                >
                  ← Edit transcript
                </button>
                <button
                  type="button"
                  className="dialogue-bulk-btn generate"
                  onClick={() => void handleTranslateAll()}
                  disabled={translating}
                >
                  {translating
                    ? React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                        className: 'spinner',
                        size: 14,
                      })
                    : 'Get 3 translations for all'}
                </button>
                <button
                  type="button"
                  className="dialogue-bulk-btn save"
                  onClick={() => void handleSaveAll()}
                  disabled={saving}
                >
                  {saving
                    ? React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                        className: 'spinner',
                        size: 14,
                      })
                    : (
                      <>
                        {React.createElement(FiCheck as React.ComponentType<{ size?: number }>, { size: 14 })}
                        Confirm & save all
                      </>
                    )}
                </button>
              </div>

              <div className="dialogue-bulk-items">
                {items.map((item, idx) => (
                  <div key={item.audio_id} className="dialogue-bulk-item">
                    <div className="dialogue-bulk-item-head">
                      <span className="dialogue-bulk-item-num">{idx + 1}.</span>
                      <span className="dialogue-bulk-item-file">{item.file_name}</span>
                      <span className="dialogue-bulk-item-lang">
                        {langLabel(item.source_lang)} → {langLabel(item.target_lang)}
                      </span>
                    </div>
                    <label>Source text</label>
                    <textarea
                      rows={2}
                      value={item.source_text}
                      onChange={(e) => updateItemSource(item.audio_id, e.target.value)}
                    />
                    {item.error && <div className="dialogue-bulk-item-error">{item.error}</div>}
                    {item.translations.length > 0 && (
                      <div className="dialogue-bulk-translations">
                        <label>Translations</label>
                        {item.translations.map((t, ti) => (
                          <div key={ti} className="dialogue-bulk-trans-row">
                            <span>{ti + 1}.</span>
                            <input
                              value={t}
                              onChange={(e) => updateTranslation(item.audio_id, ti, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DialogueBulkContentModal;
